import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API, authHeaders } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, Phone, MessageCircle, Save } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["NEW", "CONTACTED", "QUOTATION SENT", "NEGOTIATION", "SAMPLE REQUESTED", "ORDER CONFIRMED", "CLOSED", "LOST"];

const Field = ({ label, children }) => (
  <div className="py-2 border-b border-[#004225]/10 flex justify-between gap-4 text-sm">
    <span className="text-[#171717]/50">{label}</span>
    <span className="text-right font-medium text-[#171717]">{children || "—"}</span>
  </div>
);

const AdminBuyerDetail = () => {
  const { id } = useParams();
  const [all, setAll] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/enquiries`, authHeaders())
      .then((r) => { setAll(r.data); setLoaded(true); })
      .catch(() => toast.error("Could not load enquiry"));
  };
  useEffect(() => { load(); }, [id]);

  const enquiry = useMemo(() => all.find((e) => e.id === id), [all, id]);
  const history = useMemo(
    () => all.filter((e) => enquiry && e.id !== enquiry.id && (e.email === enquiry.email || (e.company && e.company === enquiry.company))),
    [all, enquiry]
  );

  useEffect(() => { if (enquiry) setNotes(enquiry.notes || ""); }, [enquiry]);

  const setStatus = async (status) => {
    try {
      await axios.patch(`${API}/admin/enquiries/${id}/status`, { status }, authHeaders());
      toast.success(`Status → ${status}`);
      load();
    } catch (e) { toast.error("Status update failed"); }
  };

  const saveNotes = async () => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/enquiries/${id}/notes`, { notes }, authHeaders());
      toast.success("Notes saved");
      load();
    } catch (e) { toast.error("Could not save notes"); }
    finally { setBusy(false); }
  };

  if (!loaded) return <div className="text-[#171717]/60 text-sm p-8">Loading buyer profile…</div>;
  if (!enquiry) return <div className="p-8"><div className="font-display text-2xl text-[#004225]">Enquiry not found</div><Link to="/admin" className="text-sm text-[#004225] underline">Back to dashboard</Link></div>;

  const timeline = [
    { ts: enquiry.created_at, event: "created", label: "Enquiry received" },
    ...(enquiry.activity || []).map((a) => ({
      ts: a.ts,
      label: a.event === "status" ? `Status changed: ${a.from} → ${a.to}` : "Internal notes updated",
      by: a.by,
    })),
  ].sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));

  return (
    <div data-testid="buyer-detail" className="space-y-6">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-[13px] text-[#004225]/80 hover:text-[#004225]"><ArrowLeft className="h-4 w-4" />Back to dashboard</Link>

      <div className="bg-white border border-[#004225]/10 rounded-sm p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-eyebrow text-[#B5942D]">{(enquiry.created_at || "").replace("T", " ").slice(0, 16)} · {enquiry.source || "website"}</div>
          <h2 className="font-display text-2xl text-[#004225]">{enquiry.name}</h2>
          <div className="text-sm text-[#171717]/70">{enquiry.company} · {enquiry.country}</div>
          <div className="mt-2 flex flex-wrap gap-3 text-[13px]">
            <a href={`mailto:${enquiry.email}`} className="inline-flex items-center gap-1 text-[#004225] hover:underline"><Mail className="h-3.5 w-3.5" />{enquiry.email}</a>
            <a href={`tel:${enquiry.phone}`} className="inline-flex items-center gap-1 text-[#004225] hover:underline"><Phone className="h-3.5 w-3.5" />{enquiry.phone}</a>
            <a href={`https://wa.me/${(enquiry.phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#004225] hover:underline"><MessageCircle className="h-3.5 w-3.5" />WhatsApp</a>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-eyebrow text-[#B5942D]">Status</span>
          <Select value={(enquiry.status || "NEW").toUpperCase()} onValueChange={setStatus}>
            <SelectTrigger className="w-56" data-testid="buyer-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="bg-white border border-[#004225]/10 rounded-sm p-6">
            <div className="font-display text-lg text-[#004225] mb-2">Requirement</div>
            <Field label="Destination Port">{enquiry.destination_port}</Field>
            <Field label="Incoterm">{enquiry.incoterm}</Field>
            <Field label="Packaging">{enquiry.packaging}</Field>
            <Field label="Expected Delivery">{enquiry.delivery_timeline}</Field>
            <Field label="Source Page">{enquiry.page || enquiry.source}</Field>
            <div className="mt-3 text-[10px] font-eyebrow text-[#B5942D]">Requested Products</div>
            {enquiry.items?.length ? enquiry.items.map((i, idx) => (
              <div key={idx} className="py-2 border-b border-[#004225]/5 text-[13px]">
                <span className="font-medium text-[#004225]">{i.product_name}</span>
                <span className="text-[#171717]/60"> — {i.quantity_mt || "?"} MT · {i.packaging || "pkg TBC"} · {i.incoterm || ""} {i.destination_port || ""}</span>
              </div>
            )) : <div className="text-[13px] text-[#171717]/50 mt-1">No cart items — direct enquiry</div>}
            {enquiry.message && <div className="mt-3"><div className="text-[10px] font-eyebrow text-[#B5942D]">Message / Specification</div><p className="mt-1 text-sm text-[#171717]/80 whitespace-pre-wrap">{enquiry.message}</p></div>}
          </div>

          <div className="bg-white border border-[#004225]/10 rounded-sm p-6">
            <div className="font-display text-lg text-[#004225] mb-2">Internal Notes</div>
            <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="buyer-notes" placeholder="Private notes — never visible to buyers" />
            <Button onClick={saveNotes} disabled={busy} data-testid="save-notes-btn" className="mt-3 btn-primary rounded-full px-5 text-[11px] uppercase tracking-widest"><Save className="h-3.5 w-3.5 mr-1.5" />{busy ? "Saving…" : "Save Notes"}</Button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-[#004225]/10 rounded-sm p-6">
            <div className="font-display text-lg text-[#004225] mb-3">Enquiry History ({history.length})</div>
            {history.length === 0 && <div className="text-sm text-[#171717]/50">First enquiry from this buyer.</div>}
            {history.map((h) => (
              <Link to={`/admin/enquiries/${h.id}`} key={h.id} className="block py-2 border-b border-[#004225]/5 text-[13px] hover:bg-[#F7F2E2]/50 px-2 -mx-2 rounded-sm">
                <span className="text-[#171717]/50">{(h.created_at || "").slice(0, 10)}</span> · {h.items?.length ? h.items[0].product_name : "Direct enquiry"}
                <span className="ml-2 text-[10px] font-eyebrow bg-[#F7F2E2] text-[#004225] px-2 py-0.5 rounded-full">{(h.status || "NEW").toUpperCase()}</span>
              </Link>
            ))}
          </div>

          <div className="bg-white border border-[#004225]/10 rounded-sm p-6">
            <div className="font-display text-lg text-[#004225] mb-3">Activity Timeline</div>
            <div className="space-y-3">
              {timeline.map((t, idx) => (
                <div key={idx} className="flex gap-3 text-[13px]">
                  <div className="mt-1.5 h-2 w-2 rounded-full bg-[#D4AF37] shrink-0" />
                  <div>
                    <div className="text-[#171717]">{t.label}</div>
                    <div className="text-[11px] text-[#171717]/50">{(t.ts || "").replace("T", " ").slice(0, 16)}{t.by ? ` · ${t.by}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBuyerDetail;
